import React, { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Badge,
  Headings,
  Paragraph,
  Button,
  PrintIcon,
  DownloadIcon,
  ChatMinimizeIcon,
} from '@sentara-health/optimahealth_web';
import { useNavigate } from 'react-router-dom';
import {
  formatMedicalText,
  toSentancePascalCase,
  NavigateConst,
  DeviceTypes,
  useDeviceType,
  useMedicalClaimItem,
  getNetworkLabel,
  domainForSegment,
  useAnalytics,
} from 'common';
import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { GlobalContext } from 'context';

type ClaimHeaderCardProps = {
  claimData: ClaimResponse;
  claimId: string;
  memberName: string;
  showMedical?: boolean;
  downloadEobPdf: (id: string) => void;
  isDownloadingPdf?: boolean;
  relatedClaims: ClaimResponse[];
  mId: string;
};

const ClaimHeaderCard: React.FC<ClaimHeaderCardProps> = ({
  claimData,
  claimId,
  memberName,
  showMedical,
  downloadEobPdf,
  isDownloadingPdf,
  relatedClaims,
  mId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useMedicalClaimItem(claimData, 0);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const { getFeature } = useContext(GlobalContext);
  const isCommercialUser = getFeature?.SuperEOB;
  const { trackContentListing } = useAnalytics();
  const isMedicalClaim = showMedical !== false;

  const trackClaimsAdobeEvent = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    trackContentListing(
      contentId,
      contentTitle,
      itemPosition,
      `${domainForSegment}${isMedicalClaim ? '/home/medical-claims' : '/home/behavioral-claims'}`
    );
  };

  const handlePrint = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMedicalClaim) {
      trackClaimsAdobeEvent('1219', 'Medical_print_claim_details_click', 12);
    } else {
      trackClaimsAdobeEvent('1229', 'Behavioral_print_claim_details_click', 23);
    }
    globalThis.print();
  };

  const handleDownloadPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMedicalClaim) {
      trackClaimsAdobeEvent('1222', 'Medical_download_claim_details_click', 14);
    } else {
      trackClaimsAdobeEvent('1230', 'Behavioral_download_claim_details_click', 24);
    }
    try {
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999rem';
      tempContainer.style.top = '-9999rem';
      tempContainer.style.width = '75rem';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '1.25rem';

      const headingElement = document.querySelector('h2.print') as HTMLElement;

      const statusBadgeElements = Array.from(
        document.querySelectorAll('.rounded-pill.mt-4.print')
      );
      const statusBadge =
        statusBadgeElements.length > 0
          ? (statusBadgeElements[0] as HTMLElement)
          : null;

      const headerElement = document.querySelector(
        '.claim-details'
      ) as HTMLElement;
      const breakdownElement = document
        .querySelector('.claims-breakdown-table')
        ?.closest('.print') as HTMLElement;

      if (!headerElement || !breakdownElement) {
        return;
      }

      if (headingElement) {
        const headingClone = headingElement.cloneNode(true) as HTMLElement;
        tempContainer.appendChild(headingClone);
      }

      if (statusBadge) {
        const badgeClone = statusBadge.cloneNode(true) as HTMLElement;
        badgeClone.style.marginTop = '1rem';
        badgeClone.style.marginBottom = '1rem';
        tempContainer.appendChild(badgeClone);
      }

      const headerClone = headerElement.cloneNode(true) as HTMLElement;

      const buttonsContainer = headerClone.querySelector(
        '.d-flex.flex-wrap.gap-3'
      );
      if (buttonsContainer) {
        buttonsContainer.remove();
      }

      tempContainer.appendChild(headerClone);

      const tableClone = breakdownElement.cloneNode(true) as HTMLElement;
      tableClone.style.marginTop = '3.75rem';
      tempContainer.appendChild(tableClone);

      const serviceTypeTable = document.querySelector('.fixed-column-table');

      if (serviceTypeTable) {
        const serviceTypeParent = serviceTypeTable.closest(
          '.print'
        ) as HTMLElement;

        if (serviceTypeParent) {
          const serviceTypeClone = serviceTypeParent.cloneNode(
            true
          ) as HTMLElement;
          serviceTypeClone.style.marginTop = '3.75rem';

          const clonedTable = serviceTypeClone.querySelector('table');
          if (clonedTable) {
            clonedTable.style.width = '100%';
            clonedTable.style.tableLayout = 'auto';
            clonedTable.style.borderCollapse = 'collapse';
          }

          tempContainer.appendChild(serviceTypeClone);
        }
      }

      const disclaimerElement = document.querySelector('.pt-4') as HTMLElement;
      if (disclaimerElement) {
        const disclaimerClone = disclaimerElement.cloneNode(
          true
        ) as HTMLElement;
        disclaimerClone.style.marginTop = '3.75rem';
        tempContainer.appendChild(disclaimerClone);
      }

      document.body.appendChild(tempContainer);

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
      });

      tempContainer.remove();

      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = doc.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= doc.internal.pageSize.getHeight();

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= doc.internal.pageSize.getHeight();
      }

      doc.save(`${claimId}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleEobClick = () => {
    if (isCommercialUser) {
      trackClaimsAdobeEvent('1209', `${isMedicalClaim ? 'Medical' : 'Behavioral'}_Claims_view_EOB_click`, 4);
      navigate(NavigateConst.superEOBClaims, {
        state: {
          claimId: claimId,
          mId: mId,
          memberId: data?.memberId,
          memberName: memberName,
          showMedical: showMedical,
        },
      })
    } else {
      if (isMedicalClaim) {
        trackClaimsAdobeEvent('1221', 'Medical_Download_EOB_claim_click', 13);
      } else {
        trackClaimsAdobeEvent('1228', 'Behavioral_Download_EOB_claim_click', 25);
      }
      downloadEobPdf(claimId);
    }
  };

  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center mb-4 not-print">
        <Button
          iconLeft={<ChatMinimizeIcon />}
          data-testid="Backbtn"
          variant="icon_btn"
          onClick={() => globalThis.history.back()}
        >
          {t("common.back")}
        </Button>

        {isMobile ? null : (
          <div className="d-flex gap-3">
            <Button
              iconLeft={<PrintIcon color="black" />}
              data-testid="Printbtn"
              variant="icon_btn"
              onClick={handlePrint}
            >
              {t("common.print")}
            </Button>

            <Button
              iconLeft={<DownloadIcon color="black" />}
              data-testid="Downloadbtn"
              variant="icon_btn"
              onClick={handleDownloadPDF}
            >
              {t("common.download")}
            </Button>
          </div>
        )}
      </div>

      <Headings
        level={2}
        variant="bold"
        text={t("claimsSummary.claimDetails")}
        className="m-0 p-0 print"
      />

      {data?.status && (
        <div className="d-none d-md-block">
        <Badge
          status={data.status}
          labelText={t("medicalClaims.badgeStatus")}
          className="rounded-pill mt-4 print"
        />
      </div>
      )}

      <div className="claim-details mt-4 print p-md-0 px-2">
        <div
          className={`row ${isMobile ? 'border rounded-4 p-2 mb-4' : 'mb-2'}`}
        >
          <div className="col-12 col-md-6">
            <div className="d-flex mt-md-0 mt-2">
              <Paragraph
                text={t("common.memberNameWithColon")}
                variant="bold"
                className="m-0 text-nowrap"
              />
              <Paragraph
                text={toSentancePascalCase(memberName)}
                className="m-0 ms-2"
              />
            </div>

            <div className="d-flex mt-2">
              <Paragraph
                text={
                  <Trans
                    i18nKey="claimsSummary.memberIdDisplay"
                    values={{ memberId: data?.memberId ?? "" }}
                    components={{
                      strong: <strong className="me-2" />,
                    }}
                  />
                }
                className="m-0 mb-2"
              />              
            </div>
          </div>

          <div className="col-12 col-md-5 offset-md-1">
            <div className="d-flex offset-md-3">
              <Paragraph
                text={
                  <Trans
                    i18nKey="claimsSummary.serviceDatesDisplay"
                    values={{ serviceDate: data?.serviceDate ?? "" }}
                    components={{
                      strong: <strong className="me-2" />,
                    }}
                  />
                }
                className="m-0"
              />
            </div>

            <div className="d-flex mt-2 offset-md-3">
              <Paragraph
                text={
                  <Trans
                    i18nKey="claimsSummary.claimIdDisplay"
                    values={{ claimId: claimId ?? "" }}
                    components={{
                      strong: <strong className="me-2" />,
                      span: <span className="ms-2" />,
                    }}
                  />
                }
                className="m-0"
              />              
            </div>
            {data?.status && isMobile && (
            <div className="d-flex mt-3 mb-3 offset-md-3">
              <Badge
                status={data.status}
                  labelText={t('medicalClaims.badgeStatus')}
                className="rounded-pill w-auto"
              />
            </div>
            )}
          </div>
        </div>

        <hr className="my-4 d-none d-md-block" />

        <div
          className={`row ${isMobile ? 'border rounded-4 p-2 mb-4' : 'mb-2'}`}
        >
          <div className="col-12 col-md-6 mb-2">
            <div className="d-flex mt-md-0 mt-2">
              <Paragraph
                text={t("claimsSummary.typeOfService")}
                variant="bold"
                className="m-0 text-nowrap"
              />
              <Paragraph
                text={formatMedicalText(data?.typeOfCare ?? '')}
                className="m-0 ms-2"
              />
            </div>

            <div className="d-flex mt-2">
              <Paragraph
                text={t("claimsSummary.practiceName")}
                variant="bold"
                className="m-0 text-nowrap"
              />
              <Paragraph
                text={formatMedicalText(data?.practiceName ?? '')}
                className="m-0 ms-2"
              />
            </div>
          </div>

          <div className="col-12 col-md-5 offset-md-1">
            <div className="d-flex offset-md-3">
              <Paragraph
                text={t("medicalClaims.provider")}
                variant="bold"
                className="m-0"
              />
              <Paragraph
                text={formatMedicalText(data?.providerName ?? '')}
                className="m-0 ms-2"
              />
            </div>

            <div className="d-flex mt-2 offset-md-3 mb-3">
              <Paragraph
                text={t("claimsSummary.network")}
                variant="bold"
                className="m-0"
              />
              <Paragraph
                text={getNetworkLabel(
                  data?.network,
                  t('claimsSummary.inNetworkProvider'),
                  t('claimsSummary.outOfNetworkProvider')
                )}
                className="m-0 ms-2"
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-3 mt-2 mb-4">
          {data?.eobFlag ? (
            <Button
              variant="primary_btn"
              disabled={isCommercialUser ? false : isDownloadingPdf}
              onClick={() => handleEobClick()}
            >
              {isCommercialUser ? t('medicalClaims.vieweob') : t("medicalClaims.downloadEOB")}
            </Button>
          ) : null}

          {relatedClaims.length > 0 ? (
          <Button 
          variant="primary_btn"  
              onClick={() => {
                trackClaimsAdobeEvent('1211', `${isMedicalClaim ? 'Medical' : 'Behavioral'}_Claims_related_claims_click`, 5);
                navigate(NavigateConst.superRelatedClaims, {
                state: { 
                  claimId: claimId, 
                  memberId: data?.memberId,
                  mId: mId,
                    memberName: memberName,
                    showMedical: showMedical,
                    relatedClaimsTracked: true,
                  },
                })
              }}
            >
              {t("claimsSummary.viewRelatedClaims")}
          </Button>
          ) : null}

          {data.showPayBill && (
            <Button
              variant="primary_btn"
              onClick={() => {
                trackClaimsAdobeEvent('1212', `${isMedicalClaim ? 'Medical' : 'Behavioral'}_Claims_pay_your_bill_click`, 6);
                navigate(NavigateConst.paySentaraBillURL);
              }}
            >
              {t("claimsSummary.paySentaraMedicalBill")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimHeaderCard;
